import { css, cx } from "@linaria/core"
import { styled } from "@linaria/react"
import { PopoverAnchor } from "@radix-ui/react-popover"
import { trimEnd } from "lodash-es"
import { observable, runInAction } from "mobx"
import { observer } from "mobx-react-lite"
import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react"

import { Button } from "@/components/Button/Button.tsx"
import { ButtonStyles } from "@/components/Button/ButtonStyles.ts"
import { Centered } from "@/components/Centered/Centered.ts"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "@/components/DropdownMenu/DropdownMenu.tsx"
import { DropdownMenuStyles } from "@/components/DropdownMenu/DropdownMenuStyles.ts"
import { Ellipsis, EllipsisContainer } from "@/components/Ellipsis/Ellipsis.ts"
import { EnvironmentLoadingText } from "@/components/EnvironmentLoadingText/EnvironmentLoadingText.tsx"
import { PreviewReadyCountdown } from "@/components/EnvironmentLoadingText/PreviewReadyCountdown.tsx"
import { Horizontal, Vertical } from "@/components/Flex.ts"
import { INPUT_HEIGHT_PX, INPUT_PADDING_PX } from "@/components/Input/Input.tsx"
import { SecondaryTextSpan } from "@/components/SecondaryTextSpan/SecondaryTextSpan.ts"
import { SHARE_LINK_TOOLTIP_SHOWN_KEY, ShareLinkPopup } from "@/components/ShareLinkPopup/ShareLinkPopup.tsx"
import { Tooltip } from "@/components/Tooltip/Tooltip.tsx"
import { useDevMode } from "@/hooks/useDevMode.ts"
import { useModel } from "@/hooks/useModel.ts"
import { useOrgContext, usePersistence, useQueries } from "@/hooks/useRootContext.ts"
import {
  CheckmarkMedium,
  ChevronDownRegular,
  DeviceRegular,
  Icon16,
  Icon20,
  LinkMedium,
  OpenInNewTabRegular,
  PickerRegular,
  ReloadRegular,
  ScreenshotMedium,
  TerminalRegular,
  ZoomInRegular,
  ZoomOutRegular
} from "@/icons/Icons.ts"
import { pluralize } from "@/lib/displayUtils.ts"
import { copyToClipboard, openLinkInNewWindow } from "@/lib/std.ts"
import { appendTokenParams } from "@/lib/url.ts"
import { InviteTeammatesDialog } from "@/routes/auth/InviteTeammatesDialog.tsx"
import { useOrganizationPermissions } from "@/routes/projects/projectHooks.ts"
import { DevServerStatusPopup } from "@/routes/tasks/TaskViewScene/DevServerStatusPopup.tsx"
import { DevToolsPanel } from "@/routes/tasks/TaskViewScene/DevToolsPanel.tsx"
import { PREVIEW_ZOOM_STORAGE_KEY } from "@/routes/tasks/TaskViewScene/persistence.ts"
import { PreviewWidgetContent } from "@/routes/tasks/TaskViewScene/PreviewWidgetContent.tsx"
import { TaskPreviewAddressBarModel } from "@/routes/tasks/TaskViewScene/TaskPreviewAddressBarModel.ts"
import { isPreviewPortBeingListened } from "@/routes/tasks/TaskViewScene/TaskPreviewModel.ts"
import { TaskPreviewPort } from "@/routes/tasks/TaskViewScene/TaskPreviewPort.tsx"
import {
  DEVICE_PRESETS,
  type DevicePreset,
  TaskPreviewTabModel
} from "@/routes/tasks/TaskViewScene/TaskPreviewTabModel.tsx"
import { ellipsis, Rotate0, Rotate180 } from "@/styles/mixins.ts"
import * as textStyles from "@/styles/textStyles.ts"
import { monospaceFont } from "@/styles/textStyles.ts"

import animationData from "./preview-starting.json"

import type { TID } from "@/lib/client/generated/TypeDefinitions.ts"
import type { OrgContext } from "@/lib/context.ts"
import type { TaskPreviewPortModel } from "@/routes/tasks/TaskViewScene/TaskPreviewPortModel.ts"
import type { TaskViewModel } from "@/routes/tasks/TaskViewScene/TaskViewModel.ts"
import type { IObservableValue } from "mobx"
import type { ClipboardEvent } from "react"

export const TaskPreviewTab = observer((props: {
  model: TaskViewModel
}) => {
  const ctx = useOrgContext()

  const selectedPreviewId = props.model.currentPreviewId.get() || null
  const isMultiPreview = props.model.isMultiPreview.get()

  const previewModel = props.model.currentPreview.get() || null
  const previewTabModel = selectedPreviewId ? (props.model.previewTabModels.get(selectedPreviewId)) : undefined

  const selectedPreviewPort = previewModel?.portModel?.previewPort?.get()

  const state = previewModel && previewModel.previewProcessesState.get()

  const devMode = useDevMode()
  const persistence = usePersistence()
  const project = props.model.project.get()

  useEffect(() => {
    if (state && state.className === "Running" && isPreviewPortBeingListened(state)) {
      // agent-determined relative path is disabled - do not automatically navigate to guessed URL
      const previewUrlRelativePath = ""

      const navigate = (url: string) => {
        runInAction(() => {
          if (previewTabModel) {
            return previewTabModel.currentUrl.set(url)
          }

          if (selectedPreviewId) {
            const model = new TaskPreviewTabModel(
              props.model.taskId,
              project,
              url,
              props.model.inputModel,
              props.model.currentVersionCommitId,
              persistence,
              devMode
            )
            props.model.previewTabModels.set(selectedPreviewId, model)
            props.model.previewTabModel.set(model)
          }
        })
      }

      const url = new URL(trimEnd(state.url, "/") + previewUrlRelativePath)
      void buildPreviewUrl(ctx, url).then(navigate)
    } else {
      // Port is not being listened - clear the preview tab model
      if (previewTabModel) {
        runInAction(() => {
          previewTabModel.terminate()
          if (selectedPreviewId) {
            props.model.previewTabModels.delete(selectedPreviewId)
          }
          if (props.model.previewTabModel.get() === previewTabModel) {
            props.model.previewTabModel.set(null)
          }
        })
      }
    }
  }, [ctx, persistence, devMode, previewModel?.agentDeterminedRelativePath, previewTabModel, props.model.currentVersionCommitId, props.model.inputModel, props.model.previewTabModel, props.model.project, props.model.taskId, state, selectedPreviewId, props.model.previewTabModels, project])

  const showLoadingAnimation = state === null
    || (state ? state.className === "Starting" : false)
    || (previewTabModel ? !previewTabModel.loadedFirst.get() : false)

  const Lottie = lazy(() => import("lottie-react"))

  const openLogIdCommand = useMemo(() => observable.box<string | null>(null), [])

  const onOpenLog = useCallback((logId: string) => {
    runInAction(() => {
      previewModel?.devToolsOpen.set(true)
      openLogIdCommand.set(logId)
    })
  }, [openLogIdCommand, previewModel?.devToolsOpen])

  const isOnboarding = Boolean(props.model.task.get()?.initRuntimeConfigState)

  const runtimeConfig = props.model.runtimeConfig.get()

  return (
    <Container>
      {devMode && isMultiPreview && Array.isArray(runtimeConfig?.previews) && (
        <Horizontal gap="8px" alignItems="center">
          {runtimeConfig?.previews.map((preview, idx) => {
            const selected = (preview.previewPort === selectedPreviewPort)
            const label = preview?.title || `Preview ${idx + 1}`
            return (
              <Button
                key={preview?.id}
                kind={selected ? "primary" : "secondary"}
                size="small"
                onClick={() => props.model.selectPreview(preview)}
              >
                {label}
              </Button>
            )
          })}
        </Horizontal>
      )}

      <TaskPreviewAddressBar
        model={props.model}
        zoom={previewTabModel?.zoom.get()}
        setZoom={previewTabModel && (v => runInAction(() => previewTabModel.zoom.set(v)))}
        pickerActive={previewTabModel?.pickerActive.get()}
        setPickerActive={previewTabModel && (v => runInAction(() => previewTabModel.pickerActive.set(v)))}
        currentUrl={previewTabModel?.displayUrl.get()}
        setCurrentUrl={previewTabModel && (v => runInAction(() => previewTabModel.currentUrl.set(v)))}
        initialUrl={previewTabModel?.initialUrl}
        errors={previewTabModel?.caughtErrors}
        loaded={previewTabModel?.loaded.get()}
        devToolsOpen={previewModel?.devToolsOpen.get() ?? false}
        setDevToolsOpen={(open) => runInAction(() => previewModel?.devToolsOpen.set(open))}
        previewPortModel={previewModel?.portModel}
        isReadonly={props.model.readonly.get()}
        isOnboarding={isOnboarding}
      />

      <PreviewContainer devToolsOpen={previewModel?.devToolsOpen.get() ?? false}>
        <PreviewFramePlaceholder
          showLoadingAnimation={showLoadingAnimation}
          devToolsOpen={previewModel?.devToolsOpen.get() ?? false}
          pickerActive={previewTabModel?.pickerActive.get() ?? false}
          deviceMode={previewTabModel?.deviceMode.get() ?? false}
          devicePreset={previewTabModel?.devicePreset.get()}
        >
          {previewTabModel?.deviceMode.get() && (
            <DeviceToolbar
              devicePreset={previewTabModel?.devicePreset.get()}
              onDevicePresetChange={previewTabModel?.setDevicePreset}
            />
          )}

          {previewTabModel && <TaskPreviewFrame previewTabModel={previewTabModel} visible={!showLoadingAnimation} />}

          {showLoadingAnimation && (
            <>
              <Suspense>
                <Lottie
                  animationData={animationData}
                  className={PreviewFrameAnimation}
                  autoplay
                  loop
                />
              </Suspense>

              <LoadingTextOverlay>
                <Vertical alignItems="center" gap="12px">
                  <EnvironmentLoadingText customText={state?.className === "Starting" ? state!.status : null} />
                  {state?.className === "Starting" && selectedPreviewId && (
                    <PreviewReadyCountdown previewId={selectedPreviewId as TID} />
                  )}
                </Vertical>
              </LoadingTextOverlay>
            </>
          )}

          {!previewTabModel && previewModel && (
            <PreviewWidgetContentContainer>
              <PreviewWidgetContent
                model={previewModel}
                displayStatusBadge={false}
                showLoadingAnimation={showLoadingAnimation}
                onOpenLog={onOpenLog}
              />
            </PreviewWidgetContentContainer>
          )}
        </PreviewFramePlaceholder>

        {previewModel?.devToolsOpen.get() && (
          <DevToolsPanel
            errors={previewTabModel?.caughtErrors}
            setDevToolsOpen={(open) => runInAction(() => previewModel?.devToolsOpen.set(open))}
            openLogId={openLogIdCommand}
          />
        )}
      </PreviewContainer>
    </Container>
  )
})

const TaskPreviewAddressBar = observer((props: {
  model: TaskViewModel

  zoom?: number
  setZoom?: (zoom: number) => void

  pickerActive?: boolean
  setPickerActive?: (active: boolean) => void

  currentUrl?: string | null
  setCurrentUrl?: (url: string) => void

  initialUrl?: string | null

  errors?: IObservableValue<Map<string, Error>>
  loaded?: boolean
  devToolsOpen: boolean
  setDevToolsOpen: (open: boolean) => void
  previewPortModel?: TaskPreviewPortModel
  isReadonly: boolean
  isOnboarding: boolean
}) => {
  const devMode = useDevMode()
  const queries = useQueries()
  const permissions = useOrganizationPermissions()

  const addressBarModel = useModel(() => {
    return new TaskPreviewAddressBarModel(props.model, props.currentUrl, props.initialUrl, props.setCurrentUrl)
  }, [props.currentUrl, props.initialUrl, props.setCurrentUrl])

  const [copyButtonSharePopupOpen, setCopyButtonSharePopupOpen] = useState(false)
  const [addressBarSharePopupOpen, setAddressBarSharePopupOpen] = useState(false)

  const [inviteDialogOpen, setInviteDialogOpen] = useState(false)

  const [showCopied, setShowCopied] = useState(false)
  const copiedTimeoutRef = useRef<number | null>(null)

  useEffect(() => {
    return () => {
      if (copiedTimeoutRef.current !== null) {
        clearTimeout(copiedTimeoutRef.current)
      }
    }
  }, [])

  useEffect(() => {
    addressBarModel.setOnCopyCallback((e: ClipboardEvent<HTMLElement>) => {
      void copyToClipboard(`https://${(e.currentTarget as HTMLElement).innerText}`, true)
      if (permissions.canInviteUsers) {
        void queries.getProfilePreference(SHARE_LINK_TOOLTIP_SHOWN_KEY).then((shown) => {
          if (!shown) {
            setAddressBarSharePopupOpen(true)
          }
        })
      }
    })
  }, [addressBarModel, permissions.canInviteUsers, queries])

  const previewTabModel = props.model.previewTabModel.get()
  const previewIsLoaded = previewTabModel?.loaded.get() === true
  const devServer = previewTabModel?.devServer.get()

  const previewProcessesState = props.model.previewProcessesState.get()

  const addressBarLoading = (!previewProcessesState || previewProcessesState.className === "Starting" || (previewProcessesState.className === "Running" && isPreviewPortBeingListened(previewProcessesState))) && (props.loaded === false || !addressBarModel.url)
  const addressBarEditable = addressBarModel.urlPrefix !== null && addressBarModel.urlEditable !== null

  const reloadButtonDisabled = addressBarModel.urlPrefix === null || addressBarModel.urlEditable === null || !previewIsLoaded
  const screenshotButtonDisabled = props.isOnboarding || typeof addressBarModel.url !== "string" || previewTabModel?.screenshotInProgress.get() === true

  const isPickerActive = !props.isOnboarding && Boolean(props.pickerActive)

  return (
    <Vertical gap="16px">
      <AddressBarContainer>
        <Tooltip content="Reload preview">
          <Button
            kind="icon"
            disabled={reloadButtonDisabled}
            className={PreviewToolbarButton}
            onClick={() => previewTabModel?.client.reload()}><ReloadRegular /></Button>
        </Tooltip>

        <ShareLinkPopup
          align="start"
          isOpen={addressBarSharePopupOpen}
          onOpenChange={setAddressBarSharePopupOpen}
          onInviteTeammate={() => setInviteDialogOpen(true)}
        >
          <AddressBarPopoverAnchor>
            <AddressBar className={cx(
              addressBarLoading ? AddressBarLoading : null,
              addressBarModel.isFocused.get() ? AddressBarFocused : null
            )}>
              {devMode && previewTabModel && (
                <DevServerStatusPopup
                  devServer={devServer}
                  previewTabModel={previewTabModel}
                />
              )}
              {addressBarEditable
                ? (
                  <AddressBarEditableContainer>
                    <AddressBarEditable
                      ref={addressBarModel.refCallback}
                      contentEditable
                      suppressContentEditableWarning
                      onKeyDown={addressBarModel.onKeyDown}
                      onInput={addressBarModel.onInput}
                      onPaste={addressBarModel.onPaste}
                      onFocus={addressBarModel.onFocus}
                      onBlur={addressBarModel.onBlur}
                      onCopy={addressBarModel.onCopy}
                    />
                  </AddressBarEditableContainer>
                )
                : (
                  <MonospacedEllipsisContainer>
                    {addressBarModel.url || addressBarModel.zeroState.get()
                      ? (
                        <Ellipsis>
                          {addressBarModel.url?.replace(TaskPreviewAddressBarModel.PROTOCOL_REGEX, "") || addressBarModel.zeroState.get()}
                        </Ellipsis>
                      )
                      : (
                        <AnimatedPlaceholderUrl />
                      )
                    }
                  </MonospacedEllipsisContainer>
                )
              }

              <AddressBarActionButtons>
                <Tooltip content="Take screenshot" align="end">
                  <ScreenshotButton
                    kind="icon"
                    size="small"
                    disabled={screenshotButtonDisabled}
                    onClick={previewTabModel?.takeScreenshot}
                  >
                    <ScreenshotIcon />
                  </ScreenshotButton>
                </Tooltip>

                <ShareLinkPopup
                  align="center"
                  isOpen={copyButtonSharePopupOpen}
                  onOpenChange={setCopyButtonSharePopupOpen}
                  onInviteTeammate={() => setInviteDialogOpen(true)}
                >
                  <PopoverAnchor>
                    <Tooltip content="Copy link to preview" align="end">
                      <ShareButton
                        kind="icon"
                        size="small"
                        disabled={typeof addressBarModel.url !== "string"}
                        showCopied={showCopied}
                        onClick={() => {
                          if (addressBarModel.url) {
                            void copyToClipboard(addressBarModel.url, true)
                            setShowCopied(true)

                            if (permissions.canInviteUsers) {
                              void queries.getProfilePreference(SHARE_LINK_TOOLTIP_SHOWN_KEY).then((shown) => {
                                if (!shown) {
                                  setCopyButtonSharePopupOpen(true)
                                }
                              })
                            }

                            if (copiedTimeoutRef.current !== null) {
                              clearTimeout(copiedTimeoutRef.current)
                            }
                            copiedTimeoutRef.current = window.setTimeout(() => {
                              setShowCopied(false)
                              copiedTimeoutRef.current = null
                            }, 1000)
                          }
                        }}
                      >
                        {showCopied
                          ? <CheckmarkIcon className={IconAnimation} />
                          : <ShareIcon className={IconAnimation} />
                        }
                      </ShareButton>
                    </Tooltip>
                  </PopoverAnchor>
                </ShareLinkPopup>

                {permissions.canInviteUsers && (
                  <InviteTeammatesDialog
                    dialogOpen={inviteDialogOpen}
                    setDialogOpen={setInviteDialogOpen}
                  />
                )}
              </AddressBarActionButtons>
            </AddressBar>
          </AddressBarPopoverAnchor>
        </ShareLinkPopup>

        <TaskPreviewZoom zoom={props.zoom} setZoom={props.setZoom} />

        <Tooltip content="Device mode" align="end">
          <Button
            kind="icon"
            className={cx(PreviewToolbarButton, previewTabModel?.deviceMode.get() ? PreviewToolbarButtonActive : null)}
            disabled={typeof addressBarModel.url !== "string"}
            onClick={() => previewTabModel?.toggleDeviceMode()}
          >
            <DeviceRegular />
          </Button>
        </Tooltip>

        <TaskPreviewToolbar
          model={props.model}
          errors={props.errors}
          pickerActive={isPickerActive}
          setPickerActive={props.setPickerActive}
          url={addressBarModel.url}
          devToolsOpen={props.devToolsOpen}
          setDevToolsOpen={props.setDevToolsOpen}
        />
      </AddressBarContainer>

      {props.model.isConfigurationTask.get() && props.previewPortModel && (
        <Horizontal alignItems="center" justifyContent="space-between" width="100%">
          <TaskPreviewPort
            model={props.previewPortModel}
            disabled={props.isReadonly}
          />

          {props.model.showPreviewLooksGoodButton.get() && (() => {
            const isPortListening = props.previewPortModel?.isPortBeingListened.get() ?? false

            const button = (
              <Button
                kind="success"
                size="large"
                onClick={() => props.model.completePreviewRuntimeSetup()}
                disabled={!isPortListening}
              >
                <Horizontal alignItems="center" gap="4px">
                  <CheckmarkMedium className={Icon16} />
                  Preview looks good!
                </Horizontal>
              </Button>
            )

            return isPortListening ? button : (
              <Tooltip content="Connect to a valid port to continue" align="end">
                {button}
              </Tooltip>
            )
          })()}
        </Horizontal>
      )}
    </Vertical>
  )
})

const TaskPreviewFrame = observer(({ previewTabModel, visible }: {
  previewTabModel: TaskPreviewTabModel
  visible: boolean
}) => {
  const persistence = usePersistence()

  const iframeRef = (iframe: HTMLIFrameElement) => {
    previewTabModel.iframeRef = iframe

    iframe.addEventListener("load", previewTabModel.onIframeLoad)

    iframe.addEventListener("error", previewTabModel.onIframeError)

    if (iframe.clientWidth > 0 && iframe.clientWidth < document.documentElement.clientWidth && !persistence.getItem(PREVIEW_ZOOM_STORAGE_KEY)) {
      runInAction(() => {
        previewTabModel.zoom.set(iframe.clientWidth / document.documentElement.clientWidth)
      })
    }

    return () => iframe.removeEventListener("load", previewTabModel.onIframeLoad)
  }

  const deviceMode = previewTabModel.deviceMode.get()
  const devicePreset = previewTabModel.devicePreset.get()

  return (
    <PreviewFrame
      ref={iframeRef}
      zoom={previewTabModel.zoom.get()}
      visible={visible}
      pickerActive={previewTabModel.pickerActive.get()}
      deviceMode={deviceMode}
      deviceWidth={deviceMode ? devicePreset.width : undefined}
      deviceHeight={deviceMode ? devicePreset.height : undefined}
      sandbox="allow-downloads allow-forms allow-modals allow-popups allow-same-origin allow-scripts"
      src={previewTabModel.initialUrl} />
  )
})

const TaskPreviewToolbar = (props: {
  model: TaskViewModel
  errors?: IObservableValue<Map<string, Error>>
  pickerActive?: boolean
  setPickerActive?: (active: boolean) => void
  url?: string | null
  devToolsOpen: boolean
  setDevToolsOpen: (open: boolean) => void
}) => {
  const url = props.url
  const pickerActive = props.pickerActive

  const previewTabModel = props.model.previewTabModel.get()

  return (
    <TaskPreviewToolbarContainer>
      <Tooltip content="Select element" align="end">
        <Button
          kind="icon"
          active={String(pickerActive)}
          className={cx(PreviewToolbarButton, pickerActive ? PreviewToolbarButtonActive : null)}
          disabled={props.setPickerActive === undefined}
          onClick={previewTabModel?.togglePicker}>
          <PickerRegular />
        </Button>
      </Tooltip>

      <Tooltip content="Open in new tab" align="end">
        <Button
          kind="icon"
          className={PreviewToolbarButton}
          disabled={typeof url !== "string"}
          onClick={() => openLinkInNewWindow(url || "", false)}
        >
          <OpenInNewTabRegular />
        </Button>
      </Tooltip>

      <DevToolsButton
        errors={props.errors}
        devToolsOpen={props.devToolsOpen}
        setDevToolsOpen={props.setDevToolsOpen}
      />
    </TaskPreviewToolbarContainer>
  )
}

const DevToolsButton = observer((props: {
  errors?: IObservableValue<Map<string, Error>>
  devToolsOpen: boolean
  setDevToolsOpen: (open: boolean) => void
}) => {
  const errorCount = props.errors?.get().size ?? 0

  return (
    <Tooltip
      content={errorCount > 0 ? `View ${errorCount} ${pluralize("error", errorCount)}` : "Open developer tools"}
      align="end"
    >
      <Button
        kind="icon"
        className={cx(PreviewToolbarButton, errorCount > 0 ? PreviewToolbarButtonError : null, props.devToolsOpen ? PreviewToolbarButtonActive : null)}
        onClick={(e) => {
          e.currentTarget.blur()
          props.setDevToolsOpen(!props.devToolsOpen)
        }}
      >
        <TerminalRegular />
      </Button>
    </Tooltip>
  )
})

const TaskPreviewZoom = (props: {
  zoom?: number
  setZoom?: (zoom: number) => void
}) => {
  const zoom = props.zoom
  const zoomSteps = [0.5, 0.75, 1, 1.25, 1.5]

  const zoomOut = () => {
    if (zoom === undefined) return
    const lowerSteps = zoomSteps.filter((z) => z < zoom)
    if (lowerSteps.length > 0) {
      const newZoom = lowerSteps[lowerSteps.length - 1]
      props.setZoom?.(newZoom)
    }
  }

  const zoomIn = () => {
    if (zoom === undefined) return
    const higherSteps = zoomSteps.filter((z) => z > zoom)
    if (higherSteps.length > 0) {
      const newZoom = higherSteps[0]
      props.setZoom?.(newZoom)
    }
  }

  const resetZoom = () => {
    props.setZoom?.(1)
  }

  const closestZoom = zoom === undefined ? 1 : zoomSteps.reduce((prev, curr) =>
    Math.abs(curr - zoom) < Math.abs(prev - zoom) ? curr : prev
  )

  return (
    <TaskPreviewZoomContainer>
      <Tooltip content="Zoom out" align="start">
        <Button
          kind="icon"
          className={cx(PreviewToolbarButton, PreviewToolbarButtonTransparent)}
          onClick={zoomOut}
          disabled={zoom === undefined || zoom <= zoomSteps[0]}
        >
          <ZoomOutRegular />
        </Button>
      </Tooltip>

      <Tooltip content="Reset zoom">
        <ZoomLevel onClick={resetZoom}>{closestZoom * 100}%</ZoomLevel>
      </Tooltip>

      <Tooltip content="Zoom in" align="end">
        <Button
          kind="icon"
          className={cx(PreviewToolbarButton, PreviewToolbarButtonTransparent)}
          onClick={zoomIn}
          disabled={zoom === undefined || zoom >= zoomSteps[zoomSteps.length - 1]}
        >
          <ZoomInRegular />
        </Button>
      </Tooltip>
    </TaskPreviewZoomContainer>
  )
}

const DeviceToolbar = observer((props: {
  devicePreset: DevicePreset
  onDevicePresetChange: (preset: DevicePreset) => void
}) => {
  const [dropdownOpen, setDropdownOpen] = useState(false)

  return (
    <DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen}>
      <DropdownMenuTrigger asChild>
        <DeviceToolbarContainer>
          <DeviceInfo>
            <DeviceName>{props.devicePreset.name}</DeviceName>
            <DeviceDimensions>
              {props.devicePreset.width} × {props.devicePreset.height}
            </DeviceDimensions>
          </DeviceInfo>
          <ChevronDownRegular className={cx(Icon20, dropdownOpen ? Rotate180 : Rotate0)} />
        </DeviceToolbarContainer>
      </DropdownMenuTrigger>
      {dropdownOpen && (
        <DropdownMenuContent>
          {DEVICE_PRESETS.map((preset) => (
            <DropdownMenuItem
              key={preset.name}
              onClick={() => {
                props.onDevicePresetChange(preset)
                setDropdownOpen(false)
              }}
              className={cx(preset.name === props.devicePreset.name ? DropdownMenuStyles.SelectedItem : undefined)}
            >
              {preset.name} ({preset.width} × {preset.height})
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      )}
    </DropdownMenu>
  )
})

async function buildPreviewUrl(ctx: OrgContext, url: URL) {
  const token = await ctx.authController.tokenSource.token()
  return appendTokenParams(url, token.accessToken, token.expiresAt)
}

const Container = styled(Vertical)`
  gap: 24px;
  height: 100%;
`

const PreviewContainer = styled(Vertical)<{
  devToolsOpen: boolean
}>`
  height: 100%;
  overflow: hidden;
`

const AddressBarContainer = styled(Horizontal)`
  align-items: center;
  gap: 8px;
`

const AddressBarPopoverAnchor = styled(PopoverAnchor)`
  flex: 1 1 auto;
`

const AddressBar = styled(Horizontal)`
  flex: 1 1 auto;
  align-items: center;
  justify-content: space-between;
  gap: 16px;

  min-height: ${INPUT_HEIGHT_PX}px;
  padding: 0 ${INPUT_PADDING_PX}px;
  border-radius: var(--border-radius-24);
  background: var(--text-color-3);

  ${textStyles.smallerFontSize};

  line-height: 100%;
`

const AddressBarLoading = css`
  @keyframes slide {
    0% {
      background-position: 200% 0;
    }

    100% {
      background-position: -200% 0;
    }
  }

  &${AddressBar} {
    animation: slide 2s linear infinite;
    background: linear-gradient(120deg, var(--text-color-3) 25%, var(--text-color-10) 50%, var(--text-color-3) 75%);
    background-size: 200% 100%;
  }
`

const AddressBarFocused = css`
  box-shadow: inset 0 0 0 1px var(--main-color-17);
`

const AddressBarEditableContainer = styled(EllipsisContainer)`
  flex: 1 1 auto;

  ${monospaceFont};

  font-size: var(--smaller-font-size);
`

const AddressBarEditable = styled(Ellipsis)`
  &:focus {
    text-overflow: clip;
  }

  &:focus-visible {
    outline: none;
  }
`

const MonospacedEllipsisContainer = styled(EllipsisContainer)`
  ${monospaceFont};

  font-size: var(--smaller-font-size);
`

const AddressBarActionButtons = styled(Horizontal)`
  margin-right: -8px;
`

const ScreenshotButton = styled(Button)`
  width: 32px;
  height: 32px;

  opacity: 0.5;

  &:focus-visible, &:hover:not(:disabled) {
    opacity: 1;
  }
`

const ScreenshotIcon = styled(ScreenshotMedium)`
  && {
    width: 20px;
    height: 20px;
  }
`

const ShareButton = styled(Button)<{ showCopied?: boolean }>`
  width: 32px;
  height: 32px;

  opacity: ${props => props.showCopied ? 1 : 0.5};
  color: ${props => props.showCopied ? "var(--success-color)" : "var(--secondary-text-color)"};

  &:focus-visible, &:hover:not(:disabled) {
    opacity: 1;
    color: ${props => props.showCopied ? "var(--success-color)" : "var(--text-color)"};
  }
`

const IconAnimation = css`
  @keyframes icon-fade-in {
    0% {
      opacity: 0;
      transform: scale(0.8);
    }

    100% {
      opacity: 1;
      transform: scale(1);
    }
  }

  animation: icon-fade-in 0.2s ease-out;
`

const ShareIcon = styled(LinkMedium)`
  && {
    width: 20px;
    height: 20px;
  }
`

const CheckmarkIcon = styled(CheckmarkMedium)`
  && {
    width: 20px;
    height: 20px;
  }
`

const TaskPreviewToolbarContainer = styled(Horizontal)`
  gap: 8px;
`

const TaskPreviewZoomContainer = styled(Horizontal)`
  align-items: center;

  background-color: var(--text-color-3);
  border-radius: var(--border-radius-24);
`

const ZoomLevel = styled.span`
  ${textStyles.smallFontSize};

  color: var(--secondary-text-color);
  cursor: pointer;

  width: 36px;
  height: ${INPUT_HEIGHT_PX}px;
  line-height: ${INPUT_HEIGHT_PX}px;
  text-align: center;
`

const PreviewFramePlaceholder = styled(Centered)<{
  showLoadingAnimation: boolean
  devToolsOpen: boolean
  pickerActive: boolean
  deviceMode: boolean
  devicePreset?: DevicePreset
}>`
  overflow: hidden;
  position: relative;

  width: 100%;
  height: ${props => props.devToolsOpen ? "50%" : "100%"};
  min-height: ${props => props.devToolsOpen ? "auto" : "100%"};
  max-height: ${props => props.devToolsOpen ? "50%" : "100%"};

  border: ${props => props.showLoadingAnimation ? "none" : "1px solid var(--separator-color)"};
  border-radius: ${props => props.pickerActive ? "0" : "24px"};
  background: ${props => {
    if (props.showLoadingAnimation) return "var(--main-color-5)"
    if (props.deviceMode) return "var(--text-color-3)"
    return "transparent"
  }};
`

// For some reason, styled(Lottie) doesn't work
const PreviewFrameAnimation = css`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  filter: blur(60px);
  z-index: -1;
`

const LoadingTextOverlay = styled(Horizontal)`
  position: absolute;
  top: 0;
  left: 0;

  width: 100%;
  height: 100%;

  align-items: center;
  justify-content: center;

  pointer-events: none;
  z-index: 1;
`

const PreviewFrame = styled.iframe<{
  zoom: number
  visible: boolean
  pickerActive: boolean
  deviceMode: boolean
  deviceWidth?: number
  deviceHeight?: number
}>`
  width: ${props => props.deviceMode && props.deviceWidth ? `${props.deviceWidth}px` : "100%"};
  height: ${props => props.deviceMode && props.deviceHeight ? `${props.deviceHeight}px` : "100%"};
  max-width: ${props => props.deviceMode ? "calc(100% - 40px)" : "none"};
  max-height: ${props => props.deviceMode ? "calc(100% - 40px)" : "none"};

  border: ${props => props.deviceMode ? "1px solid var(--separator-color)" : "none"};
  box-shadow: ${props => props.deviceMode ? "0 4px 12px var(--text-color-10)" : "none"};

  visibility: ${props => props.visible ? "visible" : "hidden"};
  zoom: ${props => `${props.zoom}`};
`

const PreviewToolbarButton = css`
  width: 40px;
  height: 40px;

  background-color: var(--text-color-3);
  border-radius: var(--border-radius-round);
`

const PreviewToolbarButtonTransparent = css`
  background-color: transparent;
`

const PreviewToolbarButtonActive = css`
  color: var(--main-color) !important;
  background: var(--main-color-17);
`

const PreviewToolbarButtonError = css`
  &:not(.${PreviewToolbarButtonActive}) {
    color: var(--error-color) !important;
    background: var(--error-color-10);
  }
`

const DeviceToolbarContainer = styled(Horizontal)`
  position: absolute;
  top: 12px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1;

  align-items: center;
  gap: 8px;

  padding: 8px 16px;
  background: var(--background-color);
  box-shadow: 0 1px 4px var(--text-color-5);

  border: 1px solid var(--separator-color);
  border-radius: var(--border-radius-24);

  ${textStyles.smallFontSize};
`

const DeviceInfo = styled(Horizontal)`
  align-items: center;
  gap: 8px;
`

const DeviceName = styled.span`
  color: var(--text-color);
  font-weight: 500;
`

const DeviceDimensions = styled.span`
  color: var(--secondary-text-color);
`

const AnimatedPlaceholderUrl = () => {
  const backendHost = useQueries().apiClient.getHost()
  const previewHost = new URL(backendHost).host.replace("api", "app") // Previews are served from a different host in the same region

  const animatedPartRef = useRef<HTMLSpanElement>(null)

  const pattern = "xxx-xxxx-xxx"
  const chars = "abcdefghijklmnopqrstuvwxyz"

  const randomizePattern = () =>
    [...pattern]
      .map(char => (/[a-z]/i.test(char) ? chars[Math.floor(Math.random() * chars.length)] : char))
      .join("")

  const initialPattern = useMemo(() => randomizePattern(), [])

  useEffect(() => {
    const element = animatedPartRef.current
    if (!element) return

    const interval = setInterval(() => {
      element.textContent = randomizePattern()
    }, 100)

    return () => clearInterval(interval)
  }, [])

  return (
    <StyledAnimatedPlaceholderUrl>
      <span ref={animatedPartRef}>{initialPattern}</span>
      <span>.{previewHost}/</span>
    </StyledAnimatedPlaceholderUrl>
  )
}

const StyledAnimatedPlaceholderUrl = styled(SecondaryTextSpan)`
  ${ellipsis};
`

// Button style override is needed to adapt them to a purple-tinted background
const PreviewWidgetContentContainer = styled(Vertical)`
  width: 50%;
  min-width: 240px;
  max-width: 400px;
  z-index: 1;

  .${ButtonStyles.Kind.Alternative} {
    border-color: var(--text-color-10);
    background: transparent;

    &:hover:not(:disabled) {
      background: var(--text-color-5);
    }
  }
`

